import type { ItemModifiers } from './item';

// ============================================================================
// RAZAS Y SEXOS
// ============================================================================

/**
 * Identificador de raza de personaje.
 */
export type CharacterRaceId = 'human' | 'elf' | 'orc' | 'drow' | 'dwarf' | 'gnome';

/**
 * Identificador de sexo de personaje.
 */
export type CharacterSexId = 'male' | 'female';

/**
 * Tipo de mejora de raza.
 * @deprecated Reemplazado por `CharacterRaceDef.innateModifiers` (ItemModifiers
 * completos, incluyendo mods únicos). Se mantiene sólo por compatibilidad de tipos;
 * el gameplay y el display leen de `innateModifiers`. Ver docs/CHARACTER_AND_RACES.md.
 */
export type RaceBonusType = 'physDef' | 'magicDef' | 'hp' | 'mana' | 'stamina' | 'minDamage' | 'maxDamage' | 'castSpeed';

/**
 * Definición de una mejora/bonus de raza.
 * @deprecated Ver `RaceBonusType`. Usar `CharacterRaceDef.innateModifiers`.
 */
export interface RaceBonusDef {
  type: RaceBonusType;
  value: number;
  labelEs: string; // Ej: "Defensa Física"
}

export interface CharacterRaceDef {
  id: CharacterRaceId;
  labelEs: string;
  /** Descripción/lore corta para la pantalla de creación y la wiki. */
  descriptionEs?: string;
  /** @deprecated Usar `innateModifiers`. Ver docs/CHARACTER_AND_RACES.md. */
  bonuses?: RaceBonusDef[];
  /**
   * Modificadores innatos que la raza otorga al personaje, iguales a los de un item
   * (incluyen mods únicos como `talkToDead` y afinidad `damageVsKingdom` por Reino).
   * Se agregan en `BaseEntity.getAllModifiers` y aparecen como fuente propia en los
   * tooltips del panel de personaje. Ver docs/CHARACTER_AND_RACES.md.
   */
  innateModifiers?: ItemModifiers;
}

export interface CharacterSexDef {
  id: CharacterSexId;
  labelEs: string;
}

// Afinidad racial por Reino: cada raza pega +15% a UN Reino de criatura (afinidad
// innata, se muestra en creación y wiki). Es PvE (los players no tienen Reino), no
// afecta PvP. Ver docs/CREATURE_KINGDOMS.md y CHARACTER_AND_RACES.md.
const RACE_AFFINITY = 0.15;

export const CHARACTER_RACES: CharacterRaceDef[] = [
  {
    id: 'human',
    labelEs: 'Humano',
    descriptionEs:
      'Versátiles y ambiciosos, forjados en guerras entre pueblos. Golpean más fuerte a otros humanoides.',
    innateModifiers: { damageVsKingdom: { humanoid: RACE_AFFINITY } },
  },
  {
    id: 'elf',
    labelEs: 'Elfo',
    descriptionEs:
      'Guardianes ancianos del bosque. Su vínculo con la naturaleza los hace letales contra las bestias.',
    innateModifiers: { damageVsKingdom: { beast: RACE_AFFINITY } },
  },
  {
    id: 'orc',
    labelEs: 'Orco',
    descriptionEs:
      'Brutos de piel dura, cazadores incansables. +5 de defensa física y saña contra los animales.',
    innateModifiers: { physDef: 5, damageVsKingdom: { animal: RACE_AFFINITY } },
  },
  {
    id: 'drow',
    labelEs: 'Elfo Oscuro',
    descriptionEs:
      'Nacidos en la penumbra, ligados al más allá: leen y hablan con los muertos, y castigan a los demonios.',
    // Vínculo con los muertos (mod único talkToDead) + afinidad contra demonios.
    innateModifiers: { talkToDead: 1, damageVsKingdom: { demon: RACE_AFFINITY } },
  },
  {
    id: 'dwarf',
    labelEs: 'Enano',
    descriptionEs:
      'Maestros de la piedra y la forja. Conocen cada engranaje: devastan a los constructos.',
    innateModifiers: { damageVsKingdom: { construct: RACE_AFFINITY } },
  },
  {
    id: 'gnome',
    labelEs: 'Gnomo',
    descriptionEs:
      'Inventores arcanos de mente veloz (+10% velocidad de casteo). Doblegan a los elementales.',
    innateModifiers: { castSpeed: 0.10, damageVsKingdom: { elemental: RACE_AFFINITY } },
  },
];

export const CHARACTER_SEXES: CharacterSexDef[] = [
  { id: 'male', labelEs: 'Hombre' },
  { id: 'female', labelEs: 'Mujer' },
];

// ============================================================================
// FUNCIONES HELPER PARA BONUSES DE RAZA
// ============================================================================

/**
 * Obtiene todos los bonuses de una raza
 * @returns Array de bonuses o array vacío si no tiene
 */
export function getRaceBonuses(raceId: CharacterRaceId): RaceBonusDef[] {
  const race = CHARACTER_RACES.find((r) => r.id === raceId);
  return race?.bonuses ?? [];
}

/**
 * Obtiene un bonus específico de una raza por tipo
 * @returns El valor del bonus o 0 si no existe
 */
export function getRaceBonus(raceId: CharacterRaceId, type: RaceBonusType): number {
  const bonuses = getRaceBonuses(raceId);
  const bonus = bonuses.find((b) => b.type === type);
  return bonus?.value ?? 0;
}

/**
 * Modificadores innatos de una raza (ItemModifiers completos, incluyen únicos).
 * @returns Copia de los innateModifiers de la raza, o `{}` si no tiene.
 */
export function getRaceInnateModifiers(raceId: CharacterRaceId): ItemModifiers {
  const race = CHARACTER_RACES.find((r) => r.id === raceId);
  return { ...(race?.innateModifiers ?? {}) };
}

/**
 * Modificadores innatos de una clase (ItemModifiers completos, incluyen únicos).
 * @returns Copia de los innateModifiers de la clase, o `{}` si no tiene.
 */
export function getClassInnateModifiers(classId: CharacterClassId): ItemModifiers {
  const classDef = CHARACTER_CLASSES.find((c) => c.id === classId);
  return { ...(classDef?.innateModifiers ?? {}) };
}

// ============================================================================
// CONFIGURACIÓN DE CABEZAS (HEADS) POR RAZA Y SEXO
// ============================================================================

/**
 * Configuración de heads para una combinación de raza y sexo.
 * - startId: ID de la primera head (ej: 1 para head1.png)
 * - endId: ID de la última head del rango (ej: 10 para head10.png)
 * - availableCount: Cantidad de heads actualmente disponibles (puede ser menor que el rango)
 * 
 * Por ejemplo, si startId=1, endId=10, availableCount=5, significa que
 * solo head1 a head5 están disponibles para esa combinación.
 */
export interface HeadRangeConfig {
  startId: number;
  endId: number;
  availableCount: number;
}

/**
 * Mapa de configuración de heads por raza y sexo.
 * Formato: { [raceId]: { [sexId]: HeadRangeConfig } }
 * 
 * Rangos establecidos:
 * - human + male:  head1  - head10
 * - human + female: head11 - head20
 * - elf + male:    head21 - head30
 * - elf + female:  head31 - head40
 * - orc + male:    head41 - head50
 * - orc + female:  head51 - head60
 * - drow + male:   head61 - head70
 * - drow + female: head71 - head80
 * - dwarf + male:  head81 - head90
 * - dwarf + female: head91 - head100
 * - gnome + male:  head101 - head110
 * - gnome + female: head111 - head120
 */
export const HEAD_CONFIG: Record<CharacterRaceId, Record<CharacterSexId, HeadRangeConfig>> = {
  human: {
    male: { startId: 1, endId: 10, availableCount: 5 },     // head1 - head5
    female: { startId: 11, endId: 20, availableCount: 7 },  // head11 - head17
  },
  elf: {
    male: { startId: 21, endId: 30, availableCount: 9 },    // head21 - head29
    female: { startId: 31, endId: 40, availableCount: 10 }, // head31 - head40
  },
  orc: {
    male: { startId: 41, endId: 50, availableCount: 9 },    // head41 - head49
    female: { startId: 51, endId: 60, availableCount: 10 }, // head51 - head60
  },
  drow: {
    male: { startId: 61, endId: 70, availableCount: 9 },    // head61 - head69
    female: { startId: 71, endId: 80, availableCount: 10 }, // head71 - head80
  },
  dwarf: {
    male: { startId: 81, endId: 90, availableCount: 9 },    // head81 - head89
    female: { startId: 91, endId: 100, availableCount: 6 }, // head91 - head96
  },
  gnome: {
    male: { startId: 101, endId: 110, availableCount: 7 },    // head101 - head107
    female: { startId: 111, endId: 120, availableCount: 3 },  // head111 - head113
  },
};

/**
 * Obtiene los IDs de head disponibles para una combinación de raza y sexo.
 * @returns Array de números (ej: [1, 2, 3, 4, 5] para human male con 5 disponibles)
 */
export function getAvailableHeadIds(race: CharacterRaceId, sex: CharacterSexId): number[] {
  const config = HEAD_CONFIG[race]?.[sex];
  if (!config) return [];
  
  const ids: number[] = [];
  const maxId = config.startId + config.availableCount - 1;
  for (let i = config.startId; i <= maxId && i <= config.endId; i++) {
    ids.push(i);
  }
  return ids;
}

/**
 * Obtiene el nombre del archivo de head (sin extensión).
 */
export function getHeadAssetName(headId: number): string {
  return `head${headId}`;
}

// ============================================================================
// CONFIGURACIÓN DE CUERPO BASE (NAKED BODY) POR RAZA Y SEXO
// ============================================================================

/**
 * Mapa de cuerpo base (naked body) por raza y sexo.
 * Este es el cuerpo del personaje que se dibuja DEBAJO de la armadura.
 * Las armaduras con transparencia mostrarán el cuerpo debajo.
 *
 * Cada raza/sexo tiene su propio sprite desnudo (IDs 101–112). El cliente los
 * carga desde `assets/entities/races/<raza>_<sexo>.png` vía `NAKED_BODY_ASSET_PATHS`
 * (ver `client/src/sprites/BodySpriteManager.ts`); el resto del pipeline de body
 * los trata como cualquier `body<id>`.
 *
 * - human + male:   body101
 * - human + female: body102
 * - elf + male:     body103
 * - elf + female:   body104
 * - orc + male:     body105
 * - orc + female:   body106
 * - drow + male:    body107
 * - drow + female:  body108
 * - dwarf + male:   body109
 * - dwarf + female: body110
 * - gnome + male:   body111
 * - gnome + female: body112
 */
export const NAKED_BODY_CONFIG: Record<CharacterRaceId, Record<CharacterSexId, number>> = {
  human: {
    male: 101,
    female: 102,
  },
  elf: {
    male: 103,
    female: 104,
  },
  orc: {
    male: 105,
    female: 106,
  },
  drow: {
    male: 107,
    female: 108,
  },
  dwarf: {
    male: 109,
    female: 110,
  },
  gnome: {
    male: 111,
    female: 112,
  },
};

/**
 * Obtiene el ID del cuerpo base (naked body) para una combinación de raza y sexo.
 * @returns ID del body (ej: 1 para human male)
 */
export function getNakedBodyId(race: CharacterRaceId, sex: CharacterSexId): number {
  return NAKED_BODY_CONFIG[race]?.[sex] ?? 1;
}

// ============================================================================
// VALIDACIÓN DE NOMBRE DE PERSONAJE
// ============================================================================

export interface CharacterNameValidation {
  isValid: boolean;
  error?: string;
}

/**
 * Valida el nombre de un personaje según las reglas:
 * - Máximo 12 caracteres
 * - No puede comenzar ni terminar con espacios
 * - Solo letras (a-z, A-Z), números (0-9) y espacios internos
 * - Máximo 2 números en total
 * - Al menos 1 caracter
 */
export function validateCharacterName(name: string): CharacterNameValidation {
  // Verificar que no esté vacío
  if (!name || name.length === 0) {
    return { isValid: false, error: 'El nombre no puede estar vacío' };
  }

  // Verificar longitud máxima
  if (name.length > 12) {
    return { isValid: false, error: 'El nombre no puede tener más de 12 caracteres' };
  }

  // Verificar que no comience con espacio
  if (name.startsWith(' ')) {
    return { isValid: false, error: 'El nombre no puede comenzar con espacio' };
  }

  // Verificar que no termine con espacio
  if (name.endsWith(' ')) {
    return { isValid: false, error: 'El nombre no puede terminar con espacio' };
  }

  // Verificar que solo contenga letras, números y espacios
  const validCharsRegex = /^[a-zA-Z0-9 ]+$/;
  if (!validCharsRegex.test(name)) {
    return { isValid: false, error: 'El nombre solo puede contener letras, números y espacios' };
  }

  // Contar números
  const digitCount = (name.match(/[0-9]/g) || []).length;
  if (digitCount > 2) {
    return { isValid: false, error: 'El nombre no puede tener más de 2 números' };
  }

  return { isValid: true };
}

// ============================================================================
// CLASES DE PERSONAJE
// ============================================================================

/**
 * Identificador interno de clase de personaje.
 * Estos valores se persisten en base de datos (columna `class`).
 */
export type CharacterClassId =
  | 'bandit'
  | 'hunter'
  | 'paladin'
  | 'cleric'
  | 'warrior'
  | 'druid'
  | 'bard'
  | 'pirate'
  | 'rogue'
  | 'wizard'
  | 'archer'
  | 'explorer'
  | 'priest'
  | 'beastmaster'
  | 'necromancer'
  | 'monk'
  | 'shaman'
  | 'engineer'
  | 'warlock';

export interface CharacterClassStats {
  baseHp: number;
  baseMana: number;
  baseStamina: number;
  hpPerLevel: number;
  manaPerLevel: number;
  staminaPerLevel: number;
}

export interface CharacterClassDef {
  /** ID interno en inglés (para DB y lógica) */
  id: CharacterClassId;
  /** Nombre para mostrar en español en el cliente */
  labelEs: string;
  /** Descripción corta en español para mostrar debajo de la imagen de clase */
  descriptionEs: string;
  /** Stats base e incremento por nivel */
  stats: CharacterClassStats;
  /**
   * Modificadores innatos que la clase otorga (iguales a los de un item, incluyen
   * mods únicos). Se agregan en `BaseEntity.getAllModifiers` y aparecen como fuente
   * propia en los tooltips del panel de personaje. Ver docs/CHARACTER_AND_RACES.md.
   */
  innateModifiers?: ItemModifiers;
}

export const CHARACTER_CLASSES: CharacterClassDef[] = [
  // Cada clase trae un modificador INNATO de "fantasía" (chico, temático) que refuerza
  // su rol en combate. Se muestra en la creación/wiki y aparece como fuente propia en la
  // ficha. Ver docs/CHARACTER_AND_RACES.md.
  { id: 'archer', labelEs: 'Arquero', descriptionEs: 'Tiradores precisos que combinan flechas con encantamientos mágicos.', stats: { baseHp: 85, baseMana: 70, baseStamina: 100, hpPerLevel: 4, manaPerLevel: 1, staminaPerLevel: 4 }, innateModifiers: { projectileDamagePercent: 0.08 } },
  { id: 'rogue', labelEs: 'Asesino', descriptionEs: 'Especialistas en sigilo, golpes críticos y desaparecer sin rastro.', stats: { baseHp: 90, baseMana: 60, baseStamina: 120, hpPerLevel: 4, manaPerLevel: 1, staminaPerLevel: 4 }, innateModifiers: { critChance: 0.05 } },
  { id: 'bandit', labelEs: 'Bandido', descriptionEs: 'Ladrones de caminos expertos en emboscadas y combate sucio.', stats: { baseHp: 90, baseMana: 50, baseStamina: 115, hpPerLevel: 4, manaPerLevel: 1, staminaPerLevel: 4 }, innateModifiers: { lifeStealPhysical: 0.03 } },
  { id: 'bard', labelEs: 'Bardo', descriptionEs: 'Músicos errantes que inspiran aliados y confunden enemigos.', stats: { baseHp: 85, baseMana: 110, baseStamina: 80, hpPerLevel: 3, manaPerLevel: 4, staminaPerLevel: 3 }, innateModifiers: { moveSpeedPercent: 0.05 } },
  { id: 'beastmaster', labelEs: 'Bestiario', descriptionEs: 'Domador de bestias salvajes. Puede montar y comandar criaturas en combate.', stats: { baseHp: 100, baseMana: 80, baseStamina: 100, hpPerLevel: 4, manaPerLevel: 4, staminaPerLevel: 3 }, innateModifiers: { damageVsKingdom: { beast: 0.10 } } },
  { id: 'warlock', labelEs: 'Brujo', descriptionEs: 'Pactos demoníacos. Usa HP para castear. Debuffs devastadores, DoTs y robo de vida.', stats: { baseHp: 100, baseMana: 90, baseStamina: 60, hpPerLevel: 5, manaPerLevel: 4, staminaPerLevel: 2 }, innateModifiers: { lifeStealMagic: 0.05 } },
  { id: 'hunter', labelEs: 'Cazador', descriptionEs: 'Rastreadores que dominan el arco y conocen cada rincón del bosque. Sin magia.', stats: { baseHp: 100, baseMana: 0, baseStamina: 130, hpPerLevel: 5, manaPerLevel: 0, staminaPerLevel: 4 }, innateModifiers: { projectileDamagePercent: 0.10 } },
  { id: 'shaman', labelEs: 'Chamán', descriptionEs: 'Conexión con los espíritus. Tótems de área, maldiciones tribales y curación ancestral.', stats: { baseHp: 95, baseMana: 110, baseStamina: 75, hpPerLevel: 3, manaPerLevel: 8, staminaPerLevel: 2 }, innateModifiers: { magicDef: 5 } },
  { id: 'cleric', labelEs: 'Clérigo', descriptionEs: 'Sacerdotes de batalla que sanan aliados y castigan a los impíos.', stats: { baseHp: 95, baseMana: 120, baseStamina: 70, hpPerLevel: 3, manaPerLevel: 8, staminaPerLevel: 2 }, innateModifiers: { magicDef: 5 } },
  { id: 'druid', labelEs: 'Druida', descriptionEs: 'Guardianes de la naturaleza que canalizan la fuerza del bosque.', stats: { baseHp: 90, baseMana: 130, baseStamina: 70, hpPerLevel: 3, manaPerLevel: 9, staminaPerLevel: 2 }, innateModifiers: { spellRangeBonus: 1 } },
  { id: 'explorer', labelEs: 'Explorador', descriptionEs: 'Híbrido sigiloso entre asesino y arquero. Versátil, ágil y letal.', stats: { baseHp: 90, baseMana: 55, baseStamina: 115, hpPerLevel: 4, manaPerLevel: 1, staminaPerLevel: 4 }, innateModifiers: { moveSpeedPercent: 0.05 } },
  { id: 'warrior', labelEs: 'Guerrero', descriptionEs: 'Combatientes frontales resistentes, expertos con armas cuerpo a cuerpo. Sin magia.', stats: { baseHp: 130, baseMana: 0, baseStamina: 100, hpPerLevel: 5, manaPerLevel: 0, staminaPerLevel: 4 }, innateModifiers: { physDef: 5 } },
  { id: 'engineer', labelEs: 'Ingeniero', descriptionEs: 'Coloca trampas, torretas y dispositivos mecánicos. Gameplay posicional y territorial. Sin magia.', stats: { baseHp: 90, baseMana: 0, baseStamina: 120, hpPerLevel: 4, manaPerLevel: 0, staminaPerLevel: 4 }, innateModifiers: { damageVsKingdom: { construct: 0.10 } } },
  { id: 'wizard', labelEs: 'Mago', descriptionEs: 'Estudiosos de lo arcano que canalizan poderosas energías mágicas.', stats: { baseHp: 80, baseMana: 150, baseStamina: 60, hpPerLevel: 3, manaPerLevel: 9, staminaPerLevel: 2 }, innateModifiers: { spellRangeBonus: 1 } },
  { id: 'monk', labelEs: 'Monje', descriptionEs: 'Maestro del combate sin armas. Alta evasión, contraataques. Sin magia.', stats: { baseHp: 105, baseMana: 0, baseStamina: 140, hpPerLevel: 5, manaPerLevel: 0, staminaPerLevel: 4 }, innateModifiers: { evasion: 0.08 } },
  { id: 'necromancer', labelEs: 'Nigromante', descriptionEs: 'Invocador de muertos que usa HP como recurso secundario. Magia oscura.', stats: { baseHp: 75, baseMana: 130, baseStamina: 55, hpPerLevel: 4, manaPerLevel: 8, staminaPerLevel: 2 }, innateModifiers: { lifeStealMagic: 0.04 } },
  { id: 'paladin', labelEs: 'Paladín', descriptionEs: 'Guerreros sagrados que combinan armadura pesada con magia de luz.', stats: { baseHp: 120, baseMana: 80, baseStamina: 80, hpPerLevel: 5, manaPerLevel: 4, staminaPerLevel: 3 }, innateModifiers: { blockChance: 0.05 } },
  { id: 'pirate', labelEs: 'Pirata', descriptionEs: 'Corsarios sin ley que viven del saqueo en mares y costas. Sin magia.', stats: { baseHp: 110, baseMana: 0, baseStamina: 100, hpPerLevel: 5, manaPerLevel: 0, staminaPerLevel: 4 }, innateModifiers: { lifeStealPhysical: 0.03 } },
  { id: 'priest', labelEs: 'Sacerdote', descriptionEs: 'Devoto sanador especializado en curación y protección. El mejor soporte.', stats: { baseHp: 85, baseMana: 140, baseStamina: 60, hpPerLevel: 3, manaPerLevel: 9, staminaPerLevel: 2 }, innateModifiers: { magicDef: 5 } },
];

/**
 * Obtiene los stats de clase por ID
 */
export function getClassStats(classId: CharacterClassId): CharacterClassStats {
  const classDef = CHARACTER_CLASSES.find(c => c.id === classId);
  // Default: stats genéricos coherentes con la banda HP 200-400@50 (ver docs/EXPERIENCE_AND_LEVELING.md)
  return classDef?.stats ?? { baseHp: 100, baseMana: 50, baseStamina: 100, hpPerLevel: 4, manaPerLevel: 3, staminaPerLevel: 4 };
}

/**
 * Clases SIN MAGIA (no usan maná). Es un CONCEPTO de diseño, no sólo la
 * consecuencia del stat: se consulta O(1) contra este set (ej. bono de
 * diversidad de party, ver docs/PARTY_SYSTEM.md). Hoy coinciden con `baseMana 0`,
 * pero esta lista es la AUTORIDAD del concepto — si una clase sin magia recibiera
 * algo de maná por utilidad, seguiría siendo "sin magia".
 */
export const NON_MAGIC_CLASS_IDS: ReadonlySet<CharacterClassId> = new Set<CharacterClassId>([
  'hunter',
  'monk',
  'warrior',
  'engineer',
  'pirate',
]);

/** True si la clase no usa maná (es "sin magia"). Lookup O(1). */
export function isNonMagicClass(classId: CharacterClassId): boolean {
  return NON_MAGIC_CLASS_IDS.has(classId);
}

/**
 * Devuelve la etiqueta en español de una clase. Fallback al ID si no hay match.
 */
export function getClassDisplayName(classId: string): string {
  const classDef = CHARACTER_CLASSES.find(c => c.id === classId);
  return classDef?.labelEs ?? classId;
}

/**
 * Calcula los stats de un personaje según clase y nivel
 */
export function getClassStatsAtLevel(classId: CharacterClassId, level: number): { maxHp: number; maxMana: number; maxStamina: number } {
  const s = getClassStats(classId);
  return {
    maxHp: s.baseHp + s.hpPerLevel * (level - 1),
    maxMana: s.baseMana + s.manaPerLevel * (level - 1),
    maxStamina: s.baseStamina + s.staminaPerLevel * (level - 1),
  };
}


